import { type ApiResponse } from '@/core/api/types';
import { type LoginResponse } from '@/services/security/models';
import { refreshAccessToken } from '@/services/security/SecurityService';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useRefreshTokenStore = defineStore('refreshToken', () => {
  const refreshTokenPromise = ref<Promise<ApiResponse<LoginResponse>> | null>(null);
  const requestCount = ref(0);

  async function getRefreshTokenPromise(
    token: string
  ): Promise<ApiResponse<LoginResponse>> {
    requestCount.value++;
    if (refreshTokenPromise.value) {
      return refreshTokenPromise.value.finally(() => {
        requestCount.value--;
        if (requestCount.value === 0) {
          resetRefreshTokenPromise();
        }
      });
    }
    const { promise, resolveFunction } = createRefreshTokenPromise<
      ApiResponse<LoginResponse> & { abortRequest?: boolean }
    >();
    refreshTokenPromise.value = promise;
    const result = await refreshAccessToken(token);
    resolveFunction(result);
    return refreshTokenPromise.value.finally(() => {
      requestCount.value--;
      if (requestCount.value === 0) {
        resetRefreshTokenPromise();
      }
    });
  }

  function createRefreshTokenPromise<T>(): {
    promise: Promise<T>;
    resolveFunction: (value: T) => void;
    rejectFunction: (value: T) => void;
  } {
    let resolveFunction: (value: T) => void = () => {};
    let rejectFunction: (value: T) => void = () => {};

    const promise = new Promise<T>((resolve, reject) => {
      resolveFunction = resolve;
      rejectFunction = reject;
    });

    return { promise, resolveFunction, rejectFunction };
  }

  function resetRefreshTokenPromise(): void {
    refreshTokenPromise.value = null;
  }

  return {
    getRefreshTokenPromise,
    resetRefreshTokenPromise
  };
});
